import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";

const MY_SAVED_SEARCHES_QUERY = gql`
  query MySavedSearches {
    mySavedSearches {
      id
      params
    }
  }
`;

const SAVE_SEARCH_MUTATION = gql`
  mutation SaveSearch($params: String!) {
    saveSearch(params: $params) {
      savedSearch {
        id
        params
      }
    }
  }
`;

const DELETE_SAVED_SEARCH_MUTATION = gql`
  mutation DeleteSavedSearch($id: ID!) {
    deleteSavedSearch(id: $id) {
      success
    }
  }
`;

interface SavedSearch {
  id: string;
  params: string;
}

interface Props {
  paramsString: string;
}

export function SaveSearchButton({ paramsString }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [justSaved, setJustSaved] = useState(false);

  const { data } = useQuery({
    queryKey: ["mySavedSearches"],
    queryFn: () =>
      graphqlClient.request<{ mySavedSearches: SavedSearch[] }>(MY_SAVED_SEARCHES_QUERY),
    enabled: !!user,
  });

  const matching = data?.mySavedSearches.find((s) => s.params === paramsString);

  const saveMutation = useMutation({
    mutationFn: () =>
      graphqlClient.request<{ saveSearch: { savedSearch: SavedSearch } }>(
        SAVE_SEARCH_MUTATION,
        { params: paramsString }
      ),
    onSuccess: () => {
      setJustSaved(true);
      queryClient.invalidateQueries({ queryKey: ["mySavedSearches"] });
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
      setTimeout(() => setJustSaved(false), 2400);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      graphqlClient.request(DELETE_SAVED_SEARCH_MUTATION, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mySavedSearches"] });
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
    },
  });

  const handleClick = () => {
    if (!user) {
      navigate(`/login?redirect_to=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (matching) {
      deleteMutation.mutate(matching.id);
    } else {
      saveMutation.mutate();
    }
  };

  const isSaved = !!matching;
  const isPending = saveMutation.isPending || deleteMutation.isPending;

  let label: string;
  if (justSaved) label = "Išsaugota — pranešime apie naujus";
  else if (isSaved) label = "Stebima";
  else label = "Stebėti šią paiešką";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      title={
        isSaved
          ? "Pašalinti iš stebimų paieškų"
          : "Pranešime, kai pasirodys naujų skelbimų pagal šiuos filtrus"
      }
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
        isSaved
          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
          : "border border-primary-200 bg-white text-primary-800 hover:bg-primary-50"
      }`}
    >
      <svg className="h-3.5 w-3.5" fill={isSaved ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      {label}
    </button>
  );
}
